import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUserAssetStats } from './hooks/useUserAssets';
import { UserAssetListPage } from './pages/UserAssetListPage';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { userAssetService } from './services/userAssetService';
import { getAssetTypeLabel, getAssetTypePercent, getVisibleAssetTypeCounts } from './constants';

const UserAssetDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'overview' | 'list'>(() => {
    return searchParams.get('tab') === 'list' ? 'list' : 'overview';
  });
  const { data: stats, isLoading: statsLoading } = useUserAssetStats();
  
  // State for month/year selection
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());

  const tabs = [
    { id: 'overview', label: 'Tổng quan', icon: '📊' },
    { id: 'list', label: 'Danh sách Tài sản', icon: '📋' },
  ];

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const handleExportReconciliation = async () => {
    try {
      // Fetch reconciliation data with selected month/year (default VAT 0%)
      const report = await userAssetService.getReconciliationReport({ 
        status: 'active', 
        vat_rate: 0,
        month: selectedMonth,
        year: selectedYear
      });

      const rows = report?.data || [];

      // Build worksheet data (title + header + rows + total)
      const title = `Biên bản đối soát kích hoạt sách trên hệ thống EBD LEARNING Tháng ${selectedMonth}/${selectedYear}`;
      
      const header = [
        'STT',
        'Trường',
        'Sách',
        'Số lượng',
        'Đơn giá',
        'Thành tiền (Trước VAT)',
        'Thuế (VAT)',
        'Thành tiền (sau VAT)'
      ];

      const sheetData: any[][] = [
        [title], // Title row
        header // Header row
      ];

      rows.forEach((r: any, idx: number) => {
        sheetData.push([
          idx + 1,
          r.school,
          r.book,
          r.quantity,
          r.unit_price,
          r.amount_before_vat,
          r.vat,
          r.amount_after_vat,
        ]);
      });

      // Totals row
      const totals = report?.totals || { quantity: 0, unit_price: 0, amount_before_vat: 0, vat: 0, amount_after_vat: 0 };
      sheetData.push([
        '',
        'Tổng cộng',
        '',
        totals.quantity,
        totals.unit_price,
        totals.amount_before_vat,
        totals.vat,
        totals.amount_after_vat,
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      
      // Merge cells for title (A1:H1)
      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 7 } } // Merge title row across all columns
      ];
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 },  // STT
        { wch: 30 }, // Trường
        { wch: 40 }, // Sách
        { wch: 10 }, // Số lượng
        { wch: 15 }, // Đơn giá
        { wch: 20 }, // Thành tiền (Trước VAT)
        { wch: 15 }, // Thuế (VAT)
        { wch: 20 }  // Thành tiền (sau VAT)
      ];
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Đối soát');

      const monthStr = String(selectedMonth).padStart(2, '0');
      const fileName = `reconciliation_report_${selectedYear}_${monthStr}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error('Export reconciliation report failed:', err);
      toast.error('Xuất báo cáo thất bại');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tài sản Người dùng</h1>
            <p className="text-gray-600 mt-1">
              Quản lý và theo dõi tài sản được gán cho người dùng
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Month/Year Selection */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Tháng:</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <option key={month} value={month}>Tháng {month}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <button
              onClick={handleExportReconciliation}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Xuất báo cáo Đối soát
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'overview' | 'list')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-blue-100 text-sm">Tổng Tài sản</p>
                      <p className="text-2xl font-bold">{stats?.total || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-green-100 text-sm">Đang Hoạt động</p>
                      <p className="text-2xl font-bold">{stats?.active || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-6 text-white">
                  <div className="flex items-center">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-yellow-100 text-sm">Hết hạn</p>
                      <p className="text-2xl font-bold">{stats?.expired || 0}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 text-white">
                  <div className="flex items-center">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <p className="text-red-100 text-sm">Bị Thu hồi</p>
                      <p className="text-2xl font-bold">{stats?.revoked || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Asset Type Distribution */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Phân bố theo Loại Tài sản</h3>
                  <div className="space-y-3">
                    {stats?.by_type && getVisibleAssetTypeCounts(stats.by_type).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{getAssetTypeLabel(type)}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${getAssetTypePercent(count, stats.total)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Access Level Distribution */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Phân bố theo Cấp độ Truy cập</h3>
                  <div className="space-y-3">
                    {stats?.by_access_level && Object.entries(stats.by_access_level).map(([level, count]) => (
                      <div key={level} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 capitalize">{level.replace('_', ' ')}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-32 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-600 h-2 rounded-full" 
                              style={{ width: `${getAssetTypePercent(count, stats.total)}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-900 w-8">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Thao tác Nhanh</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link
                    to="/admin/user-assets?tab=list"
                    onClick={() => setActiveTab('list')}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h6a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Xem Tất cả Tài sản</p>
                        <p className="text-sm text-gray-500">Quản lý danh sách tài sản</p>
                      </div>
                    </div>
                  </Link>

                  <Link
                    to="/admin/activation-report"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">Báo cáo Chi tiết</p>
                        <p className="text-sm text-gray-500">Xuất báo cáo thống kê</p>
                      </div>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'list' && <UserAssetListPage />}
        </div>
      </div>
    </div>
  );
};

export default UserAssetDashboard;
